import {
  orgLocationPickerLabel,
  type OrgLocationPickerOption,
} from "@/lib/locations/orgLocationPickerOptions";
import {
  adminInputClass,
  cardDividerBorderClass,
  cardHoverSurfaceClass,
  cardMutedTextClass,
  cardTitleTextClass,
} from "@/components/common/surfaceClasses";
import type { MemberWithProfile, OrgRole, PlatformRole } from "./adminTypes";
import { ORG_ROLE_LABELS, formatMemberName, labelOrgRole, orgRolesForAddDropdown } from "./adminTypes";
import { AdminMemberAdsRunnerCheckbox } from "./AdminMemberAdsRunnerCheckbox";
import {
  AdminMemberPlantAddPicker,
  AdminMemberPlantChips,
} from "./AdminMemberPlantAssignments";

export type AdminActiveMemberRowProps = {
  m: MemberWithProfile;
  isSelf: boolean;
  isCollapsed: boolean;
  loading: boolean;
  isPlatformAdmin: boolean;
  canAssignManufacturingOrgRole: boolean;
  currentPlatformRole: string;
  editedPlatformRole: PlatformRole;
  isPlatformDirty: boolean;
  signInDate: string;
  userLocs: string[];
  locationOptions: OrgLocationPickerOption[];
  toggleMember: (userId: string) => void;
  setMemberPlatformRoleEdits: React.Dispatch<React.SetStateAction<Record<string, PlatformRole>>>;
  addRoleByUser: Record<string, OrgRole>;
  setAddRoleByUser: React.Dispatch<React.SetStateAction<Record<string, OrgRole>>>;
  addLocByUser: Record<string, string>;
  setAddLocByUser: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onUpdateMemberPlatformRole: (member: MemberWithProfile) => void;
  onBlockMember: (userId: string, name: string) => void;
  onDeactivateMembership: (member: MemberWithProfile) => void;
  onDeleteUser: (userId: string, name: string) => void;
  onRemoveOrgRole: (member: MemberWithProfile, role: OrgRole) => void;
  onAddOrgRole: (member: MemberWithProfile, role: OrgRole) => void;
  onAddUserLocation: (member: MemberWithProfile, locId: string) => void;
  onRemoveUserLocation: (member: MemberWithProfile, locId: string) => void;
  onResendInvite: (member: MemberWithProfile) => void;
  memberCapabilities?: import("@/lib/capabilities/capabilityOverrides").CapabilityOverrides;
  onSetMemberAdsRunner: (member: MemberWithProfile, value: boolean) => void;
  userPlantIds?: string[];
  plantOptions?: import("./AdminMemberPlantAssignments").AdminPlantOption[];
  addPlantByUser?: Record<string, string>;
  setAddPlantByUser?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onAddUserPlant?: (member: MemberWithProfile, plantId: string) => void;
  onRemoveUserPlant?: (member: MemberWithProfile, plantId: string) => void;
};

export function AdminActiveMemberRow(p: AdminActiveMemberRowProps) {
  const {
    m,
    isSelf,
    isCollapsed,
    loading,
    isPlatformAdmin,
    canAssignManufacturingOrgRole,
    currentPlatformRole,
    editedPlatformRole,
    isPlatformDirty,
    signInDate,
    userLocs,
    locationOptions,
    toggleMember,
    setMemberPlatformRoleEdits,
    addRoleByUser,
    setAddRoleByUser,
    addLocByUser,
    setAddLocByUser,
    onUpdateMemberPlatformRole,
    onBlockMember,
    onDeactivateMembership,
    onDeleteUser,
    onRemoveOrgRole,
    onAddOrgRole,
    onAddUserLocation,
    onRemoveUserLocation,
    onResendInvite,
    memberCapabilities,
    onSetMemberAdsRunner,
    userPlantIds = [],
    plantOptions = [],
    addPlantByUser = {},
    setAddPlantByUser,
    onAddUserPlant,
    onRemoveUserPlant,
  } = p;

  return (
    <div className={`rounded-lg border ${cardDividerBorderClass} ${cardHoverSurfaceClass}`}>
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <div className="flex flex-wrap items-center gap-1.5 min-w-0">
          <span className={`font-medium text-sm ${cardTitleTextClass}`}>{formatMemberName(m)}</span>
          {isSelf && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full dark:bg-indigo-950/50 dark:text-indigo-300">you</span>}
          {currentPlatformRole && (
            <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full dark:bg-purple-950/40 dark:text-purple-300">{currentPlatformRole}</span>
          )}
          {m.roles.map((r) => (
            <span
              key={r}
              className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800"
            >
              {labelOrgRole(r)}
              {!isCollapsed && !isSelf && (
                <button
                  type="button"
                  onClick={() => void onRemoveOrgRole(m, r as OrgRole)}
                  disabled={loading || m.roles.length <= 1}
                  className="text-indigo-400 hover:text-red-500 leading-none ml-0.5 disabled:opacity-30"
                  title={m.roles.length <= 1 ? "Can't remove last role — use Remove instead" : `Remove ${r}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
          {userLocs.map((locId) => {
            const locLabel = orgLocationPickerLabel(locationOptions, locId);
            return (
              <span
                key={locId}
                className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800"
              >
                {locLabel}
                {!isCollapsed && !isSelf && (
                  <button
                    type="button"
                    onClick={() => void onRemoveUserLocation(m, locId)}
                    disabled={loading}
                    className="text-blue-400 hover:text-red-500 leading-none ml-0.5"
                    title={`Remove ${locLabel}`}
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })}
          {(userPlantIds.length > 0 || plantOptions.length > 0) && onRemoveUserPlant ? (
            <AdminMemberPlantChips
              member={m}
              isSelf={isSelf}
              isCollapsed={isCollapsed}
              loading={loading}
              userPlantIds={userPlantIds}
              plantOptions={plantOptions}
              onRemoveUserPlant={onRemoveUserPlant}
            />
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => toggleMember(m.user_id)}
          className={`${cardMutedTextClass} hover:text-gray-600 dark:hover:text-zinc-300 text-xs px-1.5 py-0.5 rounded flex-shrink-0`}
          title={isCollapsed ? "Expand" : "Collapse"}
        >
          {isCollapsed ? "▼" : "▲"}
        </button>
      </div>

      {!isCollapsed && (
        <div className={`border-t ${cardDividerBorderClass} px-3 pb-3 pt-2 space-y-2`}>
          <div className={`text-sm ${cardMutedTextClass}`}>{m.email}</div>
          <div className="text-xs text-gray-400 dark:text-zinc-500">last sign-in: {signInDate}</div>

          <AdminMemberAdsRunnerCheckbox
            member={m}
            dbGrants={memberCapabilities}
            disabled={loading}
            onChange={onSetMemberAdsRunner}
          />

          {!isSelf && (
            <div className="flex flex-wrap items-center gap-2">
              {isPlatformAdmin && (
                <>
                  <select
                    className={`text-sm border border-purple-200 rounded-lg px-2 py-1.5 text-purple-800 ${adminInputClass}`}
                    value={editedPlatformRole}
                    onChange={(e) =>
                      setMemberPlatformRoleEdits((prev) => ({
                        ...prev,
                        [m.user_id]: e.target.value as PlatformRole,
                      }))
                    }
                    disabled={loading}
                    title="Platform role"
                  >
                    <option value="">no platform role</option>
                    <option value="platform_dev">platform_dev</option>
                    <option value="platform_admin">platform_admin</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => void onUpdateMemberPlatformRole(m)}
                    disabled={!isPlatformDirty || loading}
                    className="px-3 py-1.5 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-40"
                  >
                    Set platform
                  </button>
                </>
              )}
              {m.email?.trim() ? (
                <button
                  type="button"
                  onClick={() => onResendInvite(m)}
                  disabled={loading}
                  className="px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 text-sm hover:bg-indigo-50 disabled:opacity-40 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
                  title="Resend Supabase invite email (works while signup is not yet completed)"
                >
                  Resend invite
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => onBlockMember(m.user_id, formatMemberName(m))}
                disabled={loading}
                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm hover:bg-amber-700 disabled:opacity-40"
              >
                Block
              </button>
              <button
                type="button"
                onClick={() => void onDeactivateMembership(m)}
                disabled={loading}
                className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-sm hover:bg-red-50 disabled:opacity-40 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/40"
              >
                Remove
              </button>
              {isPlatformAdmin && (
                <button
                  type="button"
                  onClick={() => void onDeleteUser(m.user_id, formatMemberName(m))}
                  disabled={loading}
                  className="px-3 py-1.5 rounded-lg border border-red-300 text-red-600 text-sm hover:bg-red-50 disabled:opacity-40"
                >
                  Delete User
                </button>
              )}
            </div>
          )}

          <div className="inline-flex items-center gap-1">
            <select
              className={`text-xs border border-indigo-200 rounded-lg px-1.5 py-0.5 text-indigo-800 ${adminInputClass}`}
              value={addRoleByUser[m.user_id] ?? ""}
              onChange={(e) => setAddRoleByUser((prev) => ({ ...prev, [m.user_id]: e.target.value as OrgRole }))}
              disabled={loading}
            >
              <option value="">+ add role</option>
              {orgRolesForAddDropdown(canAssignManufacturingOrgRole)
                .filter((r) => !m.roles.includes(r))
                .map((r) => (
                <option key={r} value={r}>
                  {ORG_ROLE_LABELS[r]}
                </option>
              ))}
            </select>
            {addRoleByUser[m.user_id] && (
              <button
                type="button"
                onClick={() => void onAddOrgRole(m, addRoleByUser[m.user_id] as OrgRole)}
                disabled={loading}
                className="text-xs px-2 py-0.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                Add
              </button>
            )}
          </div>

          {!isSelf && (
            <div className="inline-flex items-center gap-1">
              <select
                className={`text-xs rounded-lg px-1.5 py-0.5 ${adminInputClass}`}
                value={addLocByUser[m.user_id] ?? ""}
                onChange={(e) => setAddLocByUser((prev) => ({ ...prev, [m.user_id]: e.target.value }))}
                disabled={loading}
              >
                <option value="">+ add location</option>
                {locationOptions.filter((l) => !userLocs.includes(l.id)).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label}
                  </option>
                ))}
              </select>
              {addLocByUser[m.user_id] && (
                <button
                  type="button"
                  onClick={() => void onAddUserLocation(m, addLocByUser[m.user_id])}
                  disabled={loading}
                  className="text-xs px-2 py-0.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                >
                  Add
                </button>
              )}
            </div>
          )}
          {!isSelf && setAddPlantByUser && onAddUserPlant ? (
            <AdminMemberPlantAddPicker
              member={m}
              loading={loading}
              userPlantIds={userPlantIds}
              plantOptions={plantOptions}
              addPlantByUser={addPlantByUser}
              setAddPlantByUser={setAddPlantByUser}
              onAddUserPlant={onAddUserPlant}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}
